import React from 'react';
import { Notification, NotificationPriority } from '~/libs/infrastructure/schemas/notifications';
import { ExpandedFeed } from '../../organisms/ExpandedFeed';
import { useToggle } from 'react-use';
import { HiMiniStar, HiOutlineEnvelope, HiOutlineEnvelopeOpen, HiOutlineStar } from 'react-icons/hi2';
import { humanDateWithTime, humanTime } from '~/libs/utils/format';

export interface NotificationRowProps {
	notification: Notification;
	showDate: boolean;
	onSetPriority(notificationId: string[], priority: string): void;
	onSetRead(notificationId: string[]): void;
}

export function NotificationRow({ notification, showDate, onSetPriority, onSetRead }: NotificationRowProps) {
	const [expanded, toggleExpanded] = useToggle(false);
	const {
		id,
		createdTime,
		perex,
		email,
		authenticatedRecipient: { priority, readTime },
	} = notification;

	const dateFormatShow = (date: Date) => {
		if (!showDate) {
			return humanTime(date);
		}
		return humanDateWithTime(date);
	};

	const isPriorityHigh = priority === NotificationPriority.HIGH;

	return (
		<>
			<a
				className='flex flex-row justify-start items-center w-full px-2 py-1 font-display tracking-wide rounded transition group hover:bg-gray-200 cursor-pointer'
				onClick={() => {
					toggleExpanded();
					if (notification.authenticatedRecipient.readTime === null) {
						onSetRead([notification.id]);
					}
				}}
			>
				<div className='grow-0 shrink-0'>
					{readTime !== null ? (
						<HiOutlineEnvelopeOpen className='w-4 text-gray-300' />
					) : (
						<HiOutlineEnvelope
							className='w-4 text-cPurple-900'
							onClick={(e: React.MouseEvent<SVGAElement>) => {
								e.stopPropagation();
								onSetRead([id]);
							}}
						/>
					)}
				</div>
				<div className='grow-0 shrink-0 px-1.5'>
					{isPriorityHigh ? (
						<HiMiniStar
							aria-hidden='true'
							className='relative -mt-px w-4 h-4 text-cPurple-500'
							onClick={(e: React.MouseEvent<SVGAElement>) => {
								e.stopPropagation();
								onSetPriority([id], !isPriorityHigh ? NotificationPriority.HIGH : NotificationPriority.NORMAL);
							}}
						/>
					) : (
						<HiOutlineStar
							aria-hidden='true'
							className='w-4 h-4 text-gray-400 transition hover:text-cPurple-800'
							onClick={(e: React.MouseEvent<SVGAElement>) => {
								e.stopPropagation();
								onSetPriority([id], !isPriorityHigh ? NotificationPriority.HIGH : NotificationPriority.NORMAL);
							}}
						/>
					)}
				</div>

				<span
					className={`grow-0 h-5 px-1.5 font-open lg:text-label text-labelsmall font-bold lg:leading-5 leading-5 bg-gray-100 rounded whitespace-nowrap transition tabular-nums ${
						expanded ? 'text-gray-800 bg-opacity-100' : ''
					} ${
						readTime !== null ? 'text-gray-300 bg-opacity-40 group-hover:text-gray-800' : 'text-gray-800 bg-opacity-100'
					}`}
				>
					{dateFormatShow(createdTime)}
				</span>

				<h4
					className={`grow-0 px-2 lg:text-md text-label font-semibold lg:leading-5 leading-5 whitespace-nowrap transition ${
						expanded ? '!text-cPurple-700' : ''
					} ${readTime !== null ? 'text-gray-400 group-hover:text-cPurple-700 antialiased' : 'text-cPurple-700'}`}
				>
					{email?.subject}
				</h4>

				<p
					className={`w-full grow lg:text-label text-labelsmall lg:leading-5 leading-5 font-regular truncate transition ${
						expanded ? 'opacity-0' : ''
					} ${readTime !== null ? 'text-gray-300 group-hover:text-gray-700' : 'text-gray-700'}`}
				>
					{perex}
				</p>
			</a>

			{expanded && <ExpandedFeed notification={notification} />}
		</>
	);
}
